/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  ExtendedTooltip,
  FlexContainer,
  SVGIcon,
  theme,
} from "@nordcloud/gnui";
import { CloudResourceReportStatus } from "~/generated/graphql";
import { TooltipColor } from "~/types/TooltipColor";

type Props = {
  status: CloudResourceReportStatus;
};

export function ResourceReportStatus({ status }: Props) {
  const statusInfo = getReportStatus(status);

  return (
    <FlexContainer justifyContent="center" alignItems="center">
      <ExtendedTooltip
        status={statusInfo.tooltipColor}
        caption={statusInfo.description}
        hideTimeout={0}
        position="start"
      >
        <FlexContainer justifyContent="center" alignItems="center">
          <SVGIcon
            size="md"
            name={statusInfo.icon}
            color={statusInfo.color()}
          />
        </FlexContainer>
      </ExtendedTooltip>
    </FlexContainer>
  );
}

function getReportStatus(status: CloudResourceReportStatus) {
  switch (status) {
    case CloudResourceReportStatus.Completed:
      return {
        description: "Completed",
        icon: "successFilled" as const,
        color: () => `${theme.color.background.success}`,
        tooltipColor: TooltipColor.success,
      };
    case CloudResourceReportStatus.Failed:
      return {
        description: "Failed",
        icon: "cancelFilled" as const,
        color: () => `${theme.color.background.error}`,
        tooltipColor: TooltipColor.danger,
      };
    case CloudResourceReportStatus.InProgress:
      return {
        description: "In Progress",
        icon: "inProgressFilled" as const,
        color: () => `${theme.color.background.info}`,
        tooltipColor: TooltipColor.notification,
      };
    default:
      return {
        description: "N/A",
        icon: "playFilled" as const,
        color: () => `${theme.color.support.grey}`,
      };
  }
}
